/*
 * Copyright (c) 2024, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {
    Box,
    Skeleton,
    Stack,
    Text,
    useStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import 'swiper/css'
import 'swiper/css/navigation'
import {Navigation} from 'swiper/modules'
import useOptiversal from '../../../hooks/use-optiversal'
import {Link as ChakraLink} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Swiper, SwiperSlide} from 'swiper/react'
import {Heading} from '@salesforce/retail-react-app/app/components/shared/ui'
import useTagManager from '../../../hooks/use-tag-manager'

const OptiversalLinks = ({query, maxLinks, queryType, isplp = false, onLoadingChange}) => {
    const tagManager = useTagManager()
    const {data: optiversalResponse, isLoading} = useOptiversal(
        {
            query: query,
            maxLinks: maxLinks,
            queryType: queryType
        },
        {
            keepPreviousData: true,
            enabled: !!query
        }
    )

    const styles = useStyleConfig('OptiversalLinks')
    const containerStyles = isplp
        ? {
              width: '100%',
              maxWidth: 'none',
              overflow: 'hidden',
              padding: {
                  base: '0px 15px 30px 15px',
                  md: '0px 60px 30px 60px',
                  lg: '20px 60px 30px 60px',
                  xl: '20px 180px 30px 180px'
              }
          }
        : {}

    useEffect(() => {
        onLoadingChange?.(Boolean(query) && isLoading)
    }, [query, isLoading, onLoadingChange])

    if (!query) {
        return <></>
    }

    if (isLoading) {
        return (
            <Stack
                {...styles.container}
                {...containerStyles}
                spacing="12px"
                minHeight="125px"
                aria-hidden="true"
                data-testid="optiversal-links-skeleton"
            >
                <Skeleton height="28px" width={{base: '55%', lg: '240px'}} />
                <Skeleton height="32px" width="100%" borderRadius="15px" />
            </Stack>
        )
    }

    return optiversalResponse?.results?.length ? (
        <Box {...styles.container} {...containerStyles}>
            <Heading {...styles.heading}>
                <FormattedMessage id="related.products" defaultMessage="Related Products" />
            </Heading>
            <Swiper
                navigation={true}
                slidesPerView="auto"
                spaceBetween="10px"
                modules={[Navigation]}
            >
                {optiversalResponse.results.map((link) => {
                    return (
                        <SwiperSlide key={link.url}>
                            <Box
                                onClick={() => {
                                    tagManager.optiversalClick(link.title, link.url)
                                }}
                            >
                                <ChakraLink href={link.url} {...styles.link}>
                                    <Text {...styles.linkText}>
                                        {link.title.length > 28
                                            ? `${link.title.slice(0, 28)}...`
                                            : link.title}
                                    </Text>
                                </ChakraLink>
                            </Box>
                        </SwiperSlide>
                    )
                })}
            </Swiper>
        </Box>
    ) : (
        <></>
    )
}

OptiversalLinks.propTypes = {
    query: PropTypes.string,
    isplp: PropTypes.bool,
    maxLinks: PropTypes.number,
    queryType: PropTypes.string,
    onLoadingChange: PropTypes.func
}

export default OptiversalLinks
